import { CopyableValue } from '@/components/CopyableValue/CopyableValue'
import { ALIGNED_LABEL_COLUMNS } from '@/components/DataList/alignedLabelColumns'
import { DataTag } from '@/components/DataTag/DataTag'
import { ExternalLink } from '@/components/ExternalLink/ExternalLink'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import { getColorSchemeForStatus } from '@/features/HivesOverview/util'
import type { HiveInfo } from '@/generated/http-clients/v2ApiSchemas'
import { useGetClusterSubscription } from '@/generated/http-clients/v3ApiComponents'
import { formatDateTime } from '@/helpers/timeFormatter'
import {
  Badge,
  Button,
  DataList,
  Grid,
  GridItem,
  HStack,
  Heading,
  Icon,
  Skeleton,
  Text,
} from '@chakra-ui/react'
import { ArrowRight } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

/** How many user rows show before the list collapses behind "Show all". */
const USERS_PREVIEW_LIMIT = 6

function getSubscriptionStatusColor(status?: string) {
  if (status === 'active') {
    return 'green'
  }
  if (status === 'trialing') {
    return 'orange'
  }
  if (status === 'canceled' || status === 'past_due' || status === 'unpaid') {
    return 'red'
  }
  return undefined
}

type HiveDataCardProps = {
  hive: HiveInfo
  orgId: string
  stripeId?: string
  users?: { user_id: string }[]
  routeToClusterDetails?: (orgId: string, clusterId: string) => void
}

export function HiveDataCard({
  hive,
  orgId,
  stripeId,
  users,
  routeToClusterDetails,
}: HiveDataCardProps) {
  const { t } = useTranslation('features', { useSuspense: false })

  const isStarter = hive.plan?.toLowerCase() === 'starter'

  // Name the cluster in the card heading — under a "Hives" container, every
  // card reading "Hive Data" said nothing about which hive it was. The name
  // is user-defined and optional, so fall back to the id, which always
  // exists and is what operators search on anyway.
  const cardTitle = hive.cluster_name?.trim() || hive.cluster_id

  const {
    data: subscriptionData,
    isSuccess: subscriptionIsSuccess,
    isLoading: subscriptionIsLoading,
    isError: subscriptionIsError,
  } = useGetClusterSubscription(
    {
      pathParams: {
        orgId,
        clusterId: hive.cluster_id,
      },
    },
    { enabled: isStarter },
  )

  return (
    // Subtle grey ground so each hive reads as one unit nested inside the
    // white "Hives" container, instead of a stack of cards floating on it.
    <SectionCard
      title={
        <HStack>
          <Heading
            as="h3"
            fontSize="lg"
            m={0}
            p={0}
            display="flex"
            flexDir="row"
            gap={1}
            alignItems="center"
          >
            {cardTitle}
          </Heading>
          <DataTag
            value={hive.hive_status}
            fontWeight={500}
            hasCopy={false}
            colorPalette={getColorSchemeForStatus(hive.hive_status)}
            variant="solid"
          >
            {hive.hive_status}
          </DataTag>
        </HStack>
      }
      bg="bg.muted"
      border="none"
      actions={
        <HStack gap={4} justifyContent="space-evenly" w="full">
          {Object.entries(hive.links ?? {}).map(([link, url]) => (
            <ExternalLink key={link} href={url} color="fg">
              {getHiveLinkName(link)}
            </ExternalLink>
          ))}
          {/* Only on Org Details, where this card is one of several and the
              cluster page is a level down. On the cluster page itself the card
              is the page, so there is nowhere to go — which is exactly what
              `routeToClusterDetails` being absent means. */}
          {routeToClusterDetails ? (
            <Button
              variant="solid"
              colorPalette="brand"
              size="xs"
              flexShrink={0}
              onClick={() => routeToClusterDetails(orgId, hive.cluster_id)}
            >
              {t('orgDetails.hiveData.goToCluster')}
              <Icon as={ArrowRight} />
            </Button>
          ) : null}
        </HStack>
      }
    >
      <Grid
        templateColumns={
          users
            ? { base: '1fr 1fr 1fr', '2xl': 'max-content auto max-content' }
            : { base: '1fr 1fr', '2xl': 'max-content auto' }
        }
        gap={4}
        alignItems="stretch"
      >
        <GridItem>
          <SectionCard
            title={t('orgDetails.hiveData.sections.details')}
            height="100%"
            border="none"
            titleSize="small"
          >
            <DataList.Root
              orientation={{ base: 'vertical', '2xl': 'horizontal' }}
              css={ALIGNED_LABEL_COLUMNS}
            >
              <DataList.Item>
                <DataList.ItemLabel>
                  {t('orgDetails.hiveData.clusterId')}
                </DataList.ItemLabel>
                <DataList.ItemValue>
                  <CopyableValue
                    value={hive.cluster_id}
                    onClick={
                      routeToClusterDetails
                        ? () => routeToClusterDetails(orgId, hive.cluster_id)
                        : undefined
                    }
                  />
                </DataList.ItemValue>
              </DataList.Item>

              <DataList.Item>
                <DataList.ItemLabel>
                  {t('orgDetails.hiveData.clusterType')}
                </DataList.ItemLabel>
                <DataList.ItemValue>
                  {hive.plan ? (
                    <Badge variant="subtle">{hive.plan}</Badge>
                  ) : null}
                </DataList.ItemValue>
              </DataList.Item>

              <DataList.Item>
                <DataList.ItemLabel>
                  {t('orgDetails.hiveData.apiary')}
                </DataList.ItemLabel>
                <DataList.ItemValue>
                  <CopyableValue value={hive.apiary} />
                </DataList.ItemValue>
              </DataList.Item>

              <DataList.Item>
                <DataList.ItemLabel>
                  {t('orgDetails.hiveData.clusterUrl')}
                </DataList.ItemLabel>
                <DataList.ItemValue>
                  <CopyableValue value={hive.cluster_url} />
                </DataList.ItemValue>
              </DataList.Item>

              <DataList.Item>
                <DataList.ItemLabel>
                  {t('orgDetails.hiveData.authMethod')}
                </DataList.ItemLabel>
                <DataList.ItemValue>
                  <DataTag
                    value={hive.authentication_method}
                    fontWeight={500}
                    hasCopy={false}
                  >
                    {hive.authentication_method}
                  </DataTag>
                </DataList.ItemValue>
              </DataList.Item>

              <DataList.Item>
                <DataList.ItemLabel>
                  {t('orgDetails.hiveData.features')}
                </DataList.ItemLabel>
                <DataList.ItemValue>
                  <HStack gap={1} flexWrap="wrap">
                    {hive.broker_features.length ? (
                      hive.broker_features.map((feature) => (
                        <DataTag
                          colorPalette="orange"
                          value={feature}
                          key={feature}
                          hasCopy={false}
                        >
                          {feature}
                        </DataTag>
                      ))
                    ) : (
                      <Text color="fg.muted">N/A</Text>
                    )}
                  </HStack>
                </DataList.ItemValue>
              </DataList.Item>
            </DataList.Root>
          </SectionCard>
        </GridItem>

        <GridItem>
          <SectionCard
            title={t('orgDetails.hiveData.sections.subscription')}
            height="100%"
            titleSize="small"
            border="none"
          >
            <DataList.Root
              orientation={{ base: 'vertical', '2xl': 'horizontal' }}
              css={ALIGNED_LABEL_COLUMNS}
            >
              {/* Stripe ID lives here rather than under Details: it is
                  billing identity, which is what this column is about. */}
              {stripeId && (
                <DataList.Item>
                  <DataList.ItemLabel>
                    {t('orgDetails.hiveData.stripeId')}
                  </DataList.ItemLabel>
                  <DataList.ItemValue>
                    <CopyableValue value={stripeId} />
                  </DataList.ItemValue>
                </DataList.Item>
              )}

              {subscriptionIsSuccess && subscriptionData && (
                <>
                  <DataList.Item>
                    <DataList.ItemLabel>
                      {t('orgDetails.hiveData.subscriptionId')}
                    </DataList.ItemLabel>
                    <DataList.ItemValue>
                      <CopyableValue value={subscriptionData.subscriptionId} />
                    </DataList.ItemValue>
                  </DataList.Item>

                  <DataList.Item>
                    <DataList.ItemLabel>
                      {t('orgDetails.hiveData.customerId')}
                    </DataList.ItemLabel>
                    <DataList.ItemValue>
                      <CopyableValue value={subscriptionData.customerId} />
                    </DataList.ItemValue>
                  </DataList.Item>

                  <DataList.Item>
                    <DataList.ItemLabel>
                      {t('orgDetails.hiveData.subscriptionStatus')}
                    </DataList.ItemLabel>
                    <DataList.ItemValue>
                      <DataTag
                        value={subscriptionData.status}
                        fontWeight={500}
                        hasCopy={false}
                        textTransform="capitalize"
                        colorPalette={getSubscriptionStatusColor(
                          subscriptionData.status,
                        )}
                      >
                        {subscriptionData.status}
                      </DataTag>
                    </DataList.ItemValue>
                  </DataList.Item>

                  <DataList.Item>
                    <DataList.ItemLabel>
                      {t('orgDetails.hiveData.startDate')}
                    </DataList.ItemLabel>
                    <DataList.ItemValue>
                      {formatDateTime(subscriptionData.startDate)}
                    </DataList.ItemValue>
                  </DataList.Item>

                  <DataList.Item>
                    <DataList.ItemLabel>
                      {t('orgDetails.hiveData.trialStart')}
                    </DataList.ItemLabel>
                    <DataList.ItemValue>
                      {formatDateTime(subscriptionData.trialStart)}
                    </DataList.ItemValue>
                  </DataList.Item>

                  <DataList.Item>
                    <DataList.ItemLabel>
                      {t('orgDetails.hiveData.trialEnd')}
                    </DataList.ItemLabel>
                    <DataList.ItemValue>
                      {formatDateTime(subscriptionData.trialEnd)}
                    </DataList.ItemValue>
                  </DataList.Item>

                  <DataList.Item>
                    <DataList.ItemLabel>
                      {t('orgDetails.hiveData.endDate')}
                    </DataList.ItemLabel>
                    <DataList.ItemValue>
                      {formatDateTime(subscriptionData.endedAt)}
                    </DataList.ItemValue>
                  </DataList.Item>
                </>
              )}

              {subscriptionIsLoading && (
                <DataList.Item>
                  <DataList.ItemLabel>
                    {t('orgDetails.hiveData.subscriptionId')}
                  </DataList.ItemLabel>
                  <DataList.ItemValue>
                    <Skeleton h="20px" w="200px" />
                  </DataList.ItemValue>
                </DataList.Item>
              )}

              {subscriptionIsError && (
                <DataList.Item>
                  <DataList.ItemLabel>
                    {t('orgDetails.hiveData.subscriptionId')}
                  </DataList.ItemLabel>
                  <DataList.ItemValue color="red.500">
                    {t('orgDetails.hiveData.subscriptionError')}
                  </DataList.ItemValue>
                </DataList.Item>
              )}
            </DataList.Root>

            {/* The subscription endpoint is only queried for Starter plans,
                so say why the column is empty rather than leaving a blank. */}
            {!isStarter && (
              <Text fontSize="sm" color="fg.muted">
                {t('orgDetails.hiveData.subscriptionNotApplicable')}
              </Text>
            )}
          </SectionCard>
        </GridItem>

        {users ? (
          <GridItem>
            <HiveUsersColumn users={users} />
          </GridItem>
        ) : null}
      </Grid>
    </SectionCard>
  )
}

/**
 * The users column of a hive card, with its own show-all state.
 *
 * Split out of `HiveDataCard` rather than inlined: it is the only one of the
 * three columns that owns state, and leaving it in kept the card's control
 * flow above what is comfortable to read in one pass.
 */
function HiveUsersColumn({
  users,
}: Readonly<{ users?: { user_id: string }[] }>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const [showAllUsers, setShowAllUsers] = useState(false)

  const userCount = users?.length ?? 0
  const hasHiddenUsers = userCount > USERS_PREVIEW_LIMIT
  const visibleUsers =
    showAllUsers || !hasHiddenUsers
      ? (users ?? [])
      : (users ?? []).slice(0, USERS_PREVIEW_LIMIT)

  return (
    <SectionCard
      title={t('orgDetails.hiveData.sections.users')}
      height="100%"
      border="none"
      titleSize="small"
      append={
        // A big org can have far more users than the other two columns have
        // rows, which stretched the whole hive card. Cap the list and let the
        // operator opt into the rest.
        hasHiddenUsers ? (
          <HStack justifyContent="end" p={4}>
            <Button
              variant="subtle"
              size="xs"
              alignSelf="start"
              onClick={() => setShowAllUsers((shown) => !shown)}
            >
              {showAllUsers
                ? t('orgDetails.hiveData.showFewerUsers')
                : t('orgDetails.hiveData.showAllUsers', { count: userCount })}
            </Button>
          </HStack>
        ) : null
      }
    >
      {userCount > 0 ? (
        // No label-column alignment here: these items are value-only, so a
        // two-column grid would leave an empty label column.
        <DataList.Root orientation="horizontal">
          {visibleUsers.map((user) => (
            <DataList.Item key={user.user_id}>
              <DataList.ItemValue>
                <CopyableValue value={user.user_id} />
              </DataList.ItemValue>
            </DataList.Item>
          ))}
        </DataList.Root>
      ) : (
        <Text fontSize="sm" color="fg.muted">
          {t('orgDetails.hiveData.noUsers')}
        </Text>
      )}
    </SectionCard>
  )
}

function getHiveLinkName(name: string) {
  switch (name) {
    case 'argocd':
      return 'Argo CD'
    case 'metrics':
      return 'Metrics'
    case 'normalized_messages':
      return 'Normalized Messages'
    case 'sessions':
      return 'Sessions'
    default:
      return name
  }
}
